/**
 * OCC Option Symbol Builder
 * Core logic for building OCC option symbols from underlying, side, expiration, and strike.
 */

import { coerceExcelDateToYyyyMmDd, coerceExcelNumber, coerceExcelText } from "../excel/normalize";

export interface OccSymbolInput {
  underlying: unknown;
  side: unknown;
  expiration: unknown;
  strike: unknown;
}

export interface OccSymbolResult {
  unspaced: string; // e.g., "SPY260115C00450000"
  occ21: string; // e.g., "SPY   260115C00450000" (padded root)
  parts: {
    root: string;
    yymmdd: string;
    side: "C" | "P";
    strike8: string;
  };
}

export type ParsedOccSymbol = {
  root: string;                 // "SPY"
  expirationYYMMDD: string;     // "260116"
  side: "C" | "P";              // "C" or "P"
  strike8: string;              // "00450000"
  strike: {
    mills: bigint;              // exact: strike*1000
    value: number;              // numeric (3 decimals max)
    normalized: string;         // "450", "12.5", "227.125" (no trailing zeros)
  };
  formats: {
    unspaced: string;           // canonical: "SPY260116C00450000"
    occ21: string;              // padded-root canonical: "SPY   260116C00450000"
  };
};

/**
 * Normalize underlying symbol
 * - Trim and uppercase
 * - 1-6 chars, [A-Z0-9] only
 */
export function normalizeUnderlying(value: unknown): string {
  const text = coerceExcelText(value, true);
  const trimmed = text.toUpperCase();
  if (trimmed.length === 0 || trimmed.length > 6) {
    throw new Error("Underlying must be 1-6 characters");
  }
  if (!/^[A-Z0-9]+$/.test(trimmed)) {
    throw new Error("Underlying must contain only letters and numbers");
  }
  return trimmed;
}

/**
 * Normalize side (Call/Put)
 * - Accept C|P|CALL|PUT (case-insensitive)
 * - Return "C" or "P"
 */
export function normalizeSide(value: unknown): "C" | "P" {
  const text = coerceExcelText(value, true);
  const upper = text.toUpperCase();
  if (upper === "C" || upper === "CALL") {
    return "C";
  }
  if (upper === "P" || upper === "PUT") {
    return "P";
  }
  throw new Error('Side must be "C", "P", "CALL", or "PUT"');
}

/**
 * Normalize expiration date to YYMMDD format (domain-specific)
 * - Accepts any Excel date value (serial, Date object, strings)
 * - Uses Excel date coercer to get canonical YYYY-MM-DD
 * - Converts to OCC format YYMMDD
 * - Return YYMMDD (2-digit year, 2-digit month, 2-digit day)
 */
export function normalizeExpirationToYYMMDD(value: unknown): string {
  // Use Excel date coercer to get canonical YYYY-MM-DD format
  const isoDate = coerceExcelDateToYyyyMmDd(value);
  
  // Parse YYYY-MM-DD into components
  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    throw new Error("Invalid date format after coercion");
  }
  
  const [, year, month, day] = match;
  
  // Extract last 2 digits of year for OCC format
  const yy = year.slice(-2);
  
  return yy + month + day;
}

/**
 * Normalize strike to 8-digit zero-padded string (domain-specific)
 * - Accepts any Excel number value (number or string)
 * - Uses Excel number coercer to get canonical number
 * - Up to 3 decimals (thousandths)
 * - Compute: BigInt(whole)*1000 + BigInt(frac_padded_to_3)
 * - Max: 99999.999 → 8 digits when scaled (≤ 99999999)
 * - Zero-pad to 8 digits
 */
export function normalizeStrikeTo8(value: unknown): string {
  // Use Excel number coercer to get canonical number
  const num = coerceExcelNumber(value);
  
  // Convert to string for parsing
  const str = String(num);

  // Parse decimal number
  const decimalMatch = str.match(/^(-?\d+)(?:\.(\d+))?$/);
  if (!decimalMatch) {
    throw new Error("Strike must be a valid number");
  }

  const wholeStr = decimalMatch[1];
  const fracStr = decimalMatch[2] || "";

  // Validate decimal places (up to 3)
  if (fracStr.length > 3) {
    throw new Error("Strike can have at most 3 decimal places");
  }

  // Handle negative strikes (for puts with negative strikes, though rare)
  const isNegative = wholeStr.startsWith("-");
  const wholeAbs = isNegative ? wholeStr.slice(1) : wholeStr;

  // Validate whole part (max 5 digits for 99999.999)
  if (wholeAbs.length > 5) {
    throw new Error("Strike whole part must be 5 digits or less");
  }

  // Pad fractional part to 3 digits
  const fracPadded = fracStr.padEnd(3, "0");

  // Combine: whole * 1000 + fractional
  // Use BigInt to avoid float precision issues
  const wholeBigInt = BigInt(wholeAbs);
  const fracBigInt = BigInt(fracPadded);
  const scaled = wholeBigInt * BigInt(1000) + fracBigInt;

  // Check max value (99999999 = 99999.999 * 1000)
  if (scaled > BigInt(99999999)) {
    throw new Error("Strike scaled value must be ≤ 99999999 (max strike: 99999.999)");
  }

  // Convert to string and zero-pad to 8 digits
  const scaledStr = scaled.toString();
  const padded = scaledStr.padStart(8, "0");

  return padded;
}

/**
 * Build OCC option symbol from input components
 * Returns both unspaced format and 21-char OCC format
 */
export function buildOccOptionSymbol(input: OccSymbolInput): OccSymbolResult {
  // Normalize all inputs
  const root = normalizeUnderlying(input.underlying);
  const side = normalizeSide(input.side);
  const yymmdd = normalizeExpirationToYYMMDD(input.expiration);
  const strike8 = normalizeStrikeTo8(input.strike);

  // Build unspaced symbol: ROOT + YYMMDD + (C|P) + STRIKE_8
  const unspaced = root + yymmdd + side + strike8;

  // Build 21-char OCC format: root padded to 6 with spaces + YYMMDD + (C|P) + STRIKE_8
  const rootPadded = root.padEnd(6, " ");
  const occ21 = rootPadded + yymmdd + side + strike8;

  return {
    unspaced,
    occ21,
    parts: {
      root,
      yymmdd,
      side,
      strike8,
    },
  };
}

/**
 * Normalize strike from 8-digit string back to decimal representation
 */
function normalizeStrikeFrom8(strike8: string): { mills: bigint; value: number; normalized: string } {
  const mills = BigInt(strike8);
  const value = Number(mills) / 1000; // Safe up to 99,999,999
  
  // Format with 3 decimal places, then trim trailing zeros and optional dot
  const withDecimals = value.toFixed(3);
  const normalized = withDecimals.replace(/\.?0+$/, "");
  
  return { mills, value, normalized };
}

/**
 * Parse OCC option symbol (both unspaced and OCC21 formats) into structured data
 * @param symbol - OCC option symbol string (e.g., "SPY260116C00450000" or "SPY   260116C00450000")
 * @returns Parsed symbol with root, expiration, side, strike, and canonical formats
 */
export function parseOccSymbol(symbol: string): ParsedOccSymbol {
  // Normalize input: trim and uppercase (spaces remain spaces after toUpperCase())
  const raw = String(symbol ?? "").trim().toUpperCase();
  
  // Validate non-empty
  if (raw.length === 0) {
    throw new Error("Invalid OCC symbol: symbol cannot be empty");
  }
  
  let root: string;
  let expirationYYMMDD: string;
  let side: string;
  let strike8: string;
  
  // If length === 21: Parse as OCC21 format (padded root)
  if (raw.length === 21) {
    root = raw.slice(0, 6).trimEnd().toUpperCase(); // Use .trimEnd() not .trim() to preserve leading spaces (though there shouldn't be any)
    expirationYYMMDD = raw.slice(6, 12);
    side = raw.slice(12, 13).toUpperCase();
    strike8 = raw.slice(13, 21);
  } else {
    // Parse from end (supports 1-6 char root)
    // Validate minimum length first: prevents misleading "invalid strike" errors from bad slices
    if (raw.length < 16) {
      throw new Error("Invalid OCC symbol: too short (minimum 16 characters)");
    }
    
    strike8 = raw.slice(-8);
    side = raw.slice(-9, -8).toUpperCase();
    expirationYYMMDD = raw.slice(-15, -9);
    root = raw.slice(0, raw.length - 15).toUpperCase();
  }
  
  // Validate all components (after uppercase normalization)
  // Root: 1-6 chars, [A-Z0-9] only
  if (!/^[A-Z0-9]{1,6}$/.test(root)) {
    throw new Error("Invalid OCC symbol: root must be 1-6 alphanumeric characters");
  }
  
  // Expiration: exactly 6 digits
  if (!/^\d{6}$/.test(expirationYYMMDD)) {
    throw new Error("Invalid OCC symbol: expiration must be 6 digits (YYMMDD)");
  }
  
  // Side: "C" or "P"
  if (side !== "C" && side !== "P") {
    throw new Error("Invalid OCC symbol: side must be C or P");
  }
  
  // Strike: exactly 8 digits
  if (!/^\d{8}$/.test(strike8)) {
    throw new Error("Invalid OCC symbol: strike must be 8 digits");
  }
  
  // Convert strike8 back to decimal
  const strike = normalizeStrikeFrom8(strike8);
  
  // Build canonical formats directly from parsed segments (no date conversion, no builder calls)
  const unspaced = root + expirationYYMMDD + side + strike8;
  const occ21 = root.padEnd(6, " ") + expirationYYMMDD + side + strike8;
  
  return {
    root,
    expirationYYMMDD,
    side: side as "C" | "P",
    strike8,
    strike,
    formats: {
      unspaced,
      occ21,
    },
  };
}

/**
 * Alias for buildOccOptionSymbol (for issue naming convention)
 * @param input - Option symbol input parameters
 * @returns Built OCC option symbol with unspaced and occ21 formats
 */
export const buildOccSymbol = buildOccOptionSymbol;

/**
 * Normalize contract symbol to canonical unspaced format
 * Ensures consistent normalization between add-in and worker (prevents cache key mismatches)
 * 
 * @param input - Contract symbol (with or without "O:" prefix, may have spaces)
 * @returns Canonical unspaced OCC symbol (e.g., "SPY260116C00450000")
 * @throws Error if symbol is invalid
 */
export function normalizeContractSymbol(input: string): string {
  // Trim input
  let symbol = String(input ?? "").trim();
  
  // Remove spaces
  symbol = symbol.replace(/\s+/g, "");
  
  // Strip leading "O:" prefix if present
  if (symbol.startsWith("O:")) {
    symbol = symbol.substring(2);
  }
  
  // Parse via parseOccSymbol (throws if invalid)
  const parsed = parseOccSymbol(symbol);
  
  // Return canonical unspaced format
  return parsed.formats.unspaced;
}
